import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, TextField, Button, Card, CardContent, Divider, Chip,
  IconButton, Avatar, Tabs, Tab, Dialog, DialogTitle, DialogContent,
  DialogActions, FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import { projectActions, tagActions, userActions } from '../../store';
import { tagColors, renderColor } from '../../components/tags';
import { getAvatarColor } from '../../theme/tokens';

export const ProjectSettings = () => {
  const { id: projectId } = useParams();
  const dispatch = useDispatch();
  const project = useSelector((state) => state.projects.project);
  const tags = useSelector((state) => state.tags.tags);
  const allUsers = useSelector((state) => state.users.users);

  const [activeTab, setActiveTab] = useState(0);
  // General
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  // Members
  const [selectedUserId, setSelectedUserId] = useState('');
  // Tags
  const [openTagDialog, setOpenTagDialog] = useState(false);
  const [editingTag, setEditingTag] = useState(null); // null = create mode, object = edit mode
  const [tagName, setTagName] = useState('');
  const [tagColorIdx, setTagColorIdx] = useState(0);
  useEffect(() => {
    dispatch(projectActions.getProject(projectId));
    dispatch(tagActions.getTags({ project: projectId }));
    dispatch(userActions.getUsers());
  }, [dispatch, projectId]);

  useEffect(() => {
    if (project) {
      setName(project.name || '');
      setDescription(project.description || '');
    }
  }, [project]);

  const handleSaveProject = () => dispatch(projectActions.editProject(projectId, { name, description }));

  const handleCreateTag = () => {
    dispatch(tagActions.createTag({ name: tagName, color: tagColors[tagColorIdx].color, project: projectId }));
    setOpenTagDialog(false);
    setEditingTag(null);
    setTagName('');
    setTagColorIdx(0);
  };

  const handleEditTag = () => {
    dispatch(tagActions.updateTag(editingTag._id, { name: tagName, color: tagColors[tagColorIdx].color }));
    setOpenTagDialog(false);
    setEditingTag(null);
    setTagName('');
    setTagColorIdx(0);
  };

  const openEditDialog = (tag) => {
    setEditingTag(tag);
    setTagName(tag.name);
    const idx = tagColors.findIndex((tc) => tc.color === tag.color);
    setTagColorIdx(idx >= 0 ? idx : 0);
    setOpenTagDialog(true);
  };

  const openCreateDialog = () => {
    setEditingTag(null);
    setTagName('');
    setTagColorIdx(0);
    setOpenTagDialog(true);
  };

  // TAB CONTENT rendered based on activeTab
  // Tab 0: General - name, description, save
  // Tab 1: Members - dropdown to add, list with remove
  // Tab 2: Tags - create dialog, chip list with delete
  // Tab 3: Sprints - create, list, start/complete

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: 'text.primary' }}>
        {project?.name || 'Project Settings'}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        {project?.key ? `${project.key} — ` : ''}Manage project settings
      </Typography>

      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 3 }}>
        <Tab label="General" sx={{ textTransform: 'none', fontWeight: 600 }} />
        <Tab label="Members" sx={{ textTransform: 'none', fontWeight: 600 }} />
        <Tab label="Tags" sx={{ textTransform: 'none', fontWeight: 600 }} />
      </Tabs>

      {/* General Tab */}
      {activeTab === 0 && (
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, maxWidth: 600 }}>
          <CardContent>
            <TextField fullWidth label="Project Name" value={name} onChange={(e) => setName(e.target.value)} margin="normal" />
            <TextField fullWidth label="Description" value={description} onChange={(e) => setDescription(e.target.value)} margin="normal" multiline rows={3} />
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveProject} sx={{ textTransform: 'none', fontWeight: 600 }}>Save Changes</Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Members Tab */}
      {activeTab === 1 && (
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, maxWidth: 600 }}>
          <CardContent>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Add Member</InputLabel>
                <Select value={selectedUserId} onChange={(e) => setSelectedUserId(e.target.value)} label="Add Member">
                  <MenuItem value=""><em>Select user</em></MenuItem>
                  {(allUsers || []).filter(u => !(project?.members || []).some(m => m._id === u._id)).map(u => (
                    <MenuItem key={u._id} value={u._id}>{u.name} ({u.email})</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button variant="outlined" onClick={() => {
                if (selectedUserId) { dispatch(projectActions.addMember(projectId, selectedUserId)); setSelectedUserId(''); }
              }} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>Add</Button>
            </Box>
            <Divider sx={{ mb: 2 }} />
            {(project?.members || []).map((member, idx) => (
              <Box key={member._id || idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ width: 32, height: 32, fontSize: 14, bgcolor: getAvatarColor(member._id) }}>{member.name?.[0]?.toUpperCase() || '?'}</Avatar>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{member.name || 'Unknown'}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.disabled' }}>{member.email || ''}</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label={member.role || 'member'} size="small" variant="outlined" />
                  <IconButton size="small" onClick={() => dispatch(projectActions.removeMember(projectId, member._id))} sx={{ color: 'error.main' }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            ))}
            {(!project?.members || project.members.length === 0) && (
              <Typography variant="body2" sx={{ color: 'text.disabled', textAlign: 'center', py: 2 }}>No members yet</Typography>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tags Tab */}
      {activeTab === 2 && (
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, maxWidth: 600 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Tags</Typography>
              <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={openCreateDialog} sx={{ textTransform: 'none' }}>Add Tag</Button>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {(tags || []).map(tag => {
                const c = renderColor(tag.color);
                return (
                  <Chip key={tag._id} label={tag.name}
                    onClick={() => openEditDialog(tag)}
                    onDelete={() => dispatch(tagActions.deleteTag(tag._id))}
                    deleteIcon={<DeleteIcon fontSize="small" />}
                    sx={{
                      backgroundColor: c.background, color: c.color, fontWeight: 600, cursor: 'pointer',
                      '& .MuiChip-deleteIcon': { color: c.color, opacity: 0.7, '&:hover': { opacity: 1 } },
                    }} />
                );
              })}
              {(!tags || tags.length === 0) && <Typography variant="body2" sx={{ color: 'text.disabled' }}>No tags yet</Typography>}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Tag Create/Edit Dialog */}
      <Dialog open={openTagDialog} onClose={() => { setOpenTagDialog(false); setEditingTag(null); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>
          {editingTag ? 'Edit Tag' : 'Create Tag'}
        </DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Tag Name" value={tagName} onChange={(e) => setTagName(e.target.value)} margin="normal" required
            inputProps={{ maxLength: 15 }}
            helperText={`${tagName.length}/15`}
            FormHelperTextProps={{ sx: { textAlign: 'right' } }} />
          <Typography variant="body2" sx={{ mt: 2, mb: 1, fontWeight: 500 }}>Color</Typography>
          {tagName.trim() && (
            <Box sx={{ mb: 1.5 }}>
              <Chip label={tagName} sx={{ backgroundColor: tagColors[tagColorIdx].background, color: tagColors[tagColorIdx].color, fontWeight: 600 }} />
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {tagColors.map((tc, idx) => (
              <Box key={idx} onClick={() => setTagColorIdx(idx)}
                sx={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: tc.color, cursor: 'pointer', border: tagColorIdx === idx ? '3px solid' : '3px solid transparent', borderColor: tagColorIdx === idx ? 'text.primary' : 'transparent' }} />
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setOpenTagDialog(false); setEditingTag(null); }} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={editingTag ? handleEditTag : handleCreateTag} disabled={!tagName.trim()}
            sx={{ textTransform: 'none' }}>{editingTag ? 'Save' : 'Create'}</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};
